import { toast } from 'sonner';
import { getCachedLiveOrgSettings, getOrganisationTimesheetSettings } from '@/services/settingsRepository';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { DataMode } from '@/contexts/DataSourceContext';
import type { ExtendedWorkingPattern, TimesheetEntry, WorkingPattern } from '@/types/timesheet';

export interface TimesheetSubmitValidationParams {
  screenMode: DataMode;
  entries: TimesheetEntry[];
  organizationSettings: OrganisationTimesheetSettings | undefined;
  effectiveWorkingPattern: WorkingPattern | ExtendedWorkingPattern | undefined;
  companyHasCostingIntegration: boolean;
  hasBlockingPolicyViolation: boolean;
  hasMissingDescriptions: boolean;
  isDaily: boolean;
}

/**
 * Runs every pre-submission check, showing the matching error toast for the first
 * failure. Returns true when the timesheet may be submitted.
 */
export async function canSubmitTimesheet({
  screenMode,
  entries,
  organizationSettings,
  effectiveWorkingPattern,
  companyHasCostingIntegration,
  hasBlockingPolicyViolation,
  hasMissingDescriptions,
  isDaily,
}: TimesheetSubmitValidationParams): Promise<boolean> {
  if (hasBlockingPolicyViolation) {
    toast.error('Submission blocked by policy', {
      description: 'A policy violation is preventing submission. Please review AI Insights for details.',
      duration: 5000,
    });
    return false;
  }

  if (entries.length === 0) {
    toast.error('Cannot submit empty timesheet');
    return false;
  }

  if (organizationSettings?.requireDescription === 'always' && hasMissingDescriptions) {
    toast.error(
      isDaily
        ? 'Please add daily descriptions to all entries before submitting'
        : 'Please add descriptions to all entries'
    );
    return false;
  }

  // Validate project and cost centre requirements.
  // Prefer the already-loaded organisation settings and fall back to the cache
  // then the defaults, so validation never silently no-ops when the localStorage
  // cache is empty (which previously let submissions reach the backend and fail
  // with an unhandled cost-centre validation error).
  const settings = organizationSettings
    ?? (screenMode === 'live'
      ? getCachedLiveOrgSettings()
      : await getOrganisationTimesheetSettings(screenMode))
    ?? DEFAULT_TIMESHEET_SETTINGS;

  const missingProjects = entries.filter(entry => !entry.projectId);
  if (missingProjects.length > 0) {
    toast.error('Cannot submit timesheet', {
      description: 'All entries must have a project assigned. Please add projects to all entries.',
      duration: 5000,
    });
    return false;
  }

  // Validate cost centre when it's required in settings, or when the current
  // company posts costing transactions to a financial backend (Access Financials /
  // Dimensions) — submitting without one would otherwise fail at the posting step.
  if (settings.showCostCentre !== false && (settings.requireCostCentre || companyHasCostingIntegration)) {
    const missingCostCentres = entries.filter(entry => entry.projectId && !entry.costCentreId);
    if (missingCostCentres.length > 0) {
      toast.error('Cannot submit timesheet', {
        description: 'All entries must have a cost centre assigned. Please add cost centres to all entries.',
        duration: 5000,
      });
      return false;
    }
  }

  // Only validate role if it's shown and required in settings
  if (settings?.showRole && settings?.requireRole) {
    const missingRoles = entries.filter(entry => !entry.roleId);
    if (missingRoles.length > 0) {
      toast.error('Cannot submit timesheet', {
        description: 'All entries must have a role assigned. Please add roles to all entries.',
        duration: 5000,
      });
      return false;
    }
  }

  // Check minimum hours requirement based on employee's working pattern
  const expectedWeeklyHours = effectiveWorkingPattern?.hoursPerWeek || 40;
  const totalHours = entries.reduce((sum, entry) => {
    return sum + Object.values(entry.hours).reduce((h, v) => h + v, 0);
  }, 0);

  const minimumHoursThreshold = expectedWeeklyHours * 0.8; // Allow 20% tolerance

  if (totalHours < minimumHoursThreshold) {
    toast.error(
      `Insufficient hours logged`,
      {
        description: `You have logged ${totalHours.toFixed(1)}h but need at least ${minimumHoursThreshold.toFixed(1)}h (${expectedWeeklyHours}h expected). Please add missing time entries.`,
        duration: 6000,
      }
    );
    return false;
  }

  return true;
}
